import React from "react";
import { Download, GraduationCap, ArrowRight, CheckCircle2, Loader2, Volume2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLeadMagnet } from "@/components/landing/LeadMagnet";
import { useLanguage } from "@/context/LanguageContext";

const A1_URL = "/resources/francais-a1-premiers-pas.pptx";

const COPY = {
  pt: {
    overline: "Recurso gratuito · Nível A1 iniciante",
    title: "Começando do zero ? Comece por aqui.",
    subtitle:
      "Uma apresentação de 19 slides com áudio para dar os primeiros passos em francês : saudações, alfabeto, números, tu/vous e as primeiras estruturas — feita por um parisiense com brasileiros em mente.",
    features: ["19 slides", "Áudio nativo", "PT + FR"],
    nameLabel: "Seu primeiro nome",
    emailLabel: "Seu melhor e-mail",
    cta: "Receber a apresentação",
    downloadCta: "Baixar agora",
    successTitle: "Prêt·e ? Vá em frente !",
    successBody: "A apresentação está a caminho no seu e-mail. Comece pelos slides 1–5 hoje.",
  },
  en: {
    overline: "Free resource · Level A1 beginner",
    title: "Starting from scratch? Start here.",
    subtitle:
      "A 19-slide presentation with audio to take your first steps in French: greetings, alphabet, numbers, tu/vous and your first sentence structures — built by a native Parisian with beginners in mind.",
    features: ["19 slides", "Native audio", "EN + FR"],
    nameLabel: "Your first name",
    emailLabel: "Your best email",
    cta: "Send me the presentation",
    downloadCta: "Download now",
    successTitle: "Prêt·e? Let's go!",
    successBody: "The presentation is on its way to your inbox. Start with slides 1–5 today.",
  },
  fr: {
    overline: "Ressource offerte · Niveau A1 débutant",
    title: "Vous partez de zéro ? Commencez ici.",
    subtitle:
      "Une présentation de 19 diapos avec audio pour faire vos premiers pas en français : salutations, alphabet, nombres, tu/vous et vos premières structures — conçue par un Parisien pensée pour les grands débutants.",
    features: ["19 diapos", "Audio natif", "FR + PT"],
    nameLabel: "Votre prénom",
    emailLabel: "Votre meilleur e-mail",
    cta: "Recevoir la présentation",
    downloadCta: "Télécharger",
    successTitle: "Prêt·e ? On y va !",
    successBody: "La présentation arrive dans votre boîte. Commencez par les diapos 1–5.",
  },
};

export default function LeadMagnetA1() {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  const {
    state,
    onSubmit,
    pdfUrl,
  } = useLeadMagnet({ resource: "a1_premiers_pas", downloadUrl: A1_URL });

  return (
    <section
      id="a1-premiers-pas"
      data-testid="lead-magnet-a1-section"
      className="bg-[#f4f1ea] py-20 sm:py-24 border-t border-[#e5e3db]"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1e2a5a]/5 px-3 py-1.5 text-xs text-[#1e2a5a] mb-4">
              <GraduationCap className="h-3.5 w-3.5" /> {t.overline}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.1] text-[#1e2a5a]">
              {t.title}
            </h2>
            <p className="mt-5 text-base text-[#3a3a3a] leading-relaxed">
              {t.subtitle}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {t.features.map((f, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#1e2a5a]/20 bg-white px-3 py-1 text-xs text-[#1e2a5a]"
                >
                  {i === 1 && <Volume2 className="h-3 w-3" />}
                  {f}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#e5e3db] rounded-2xl p-6 sm:p-8 shadow-sm">
            {state.success ? (
              <div data-testid="lead-magnet-a1-success" className="text-center py-4">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1e7a3e]/10 text-[#1e7a3e] mb-4">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h3 className="font-display text-xl text-[#1e2a5a] tracking-tight">
                  {t.successTitle}
                </h3>
                <p className="mt-2 text-sm text-[#5c5c5c] leading-relaxed">
                  {t.successBody}
                </p>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-magnet-a1-download"
                  className="group mt-5 inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-5 py-3 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e]"
                >
                  <Download className="h-4 w-4" /> {t.downloadCta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            ) : (
              <form onSubmit={(e) => onSubmit(e, "a1_section")} className="space-y-4">
                <Input
                  data-testid="lead-magnet-a1-name"
                  type="text"
                  required
                  placeholder={t.nameLabel}
                  autoComplete="given-name"
                  value={state.name}
                  onChange={(e) => state.setName(e.target.value)}
                  className="bg-[#f4f1ea] border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
                />
                <Input
                  data-testid="lead-magnet-a1-email"
                  type="email"
                  required
                  placeholder={t.emailLabel}
                  autoComplete="email"
                  value={state.email}
                  onChange={(e) => state.setEmail(e.target.value)}
                  className="bg-[#f4f1ea] border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
                />
                {/* Honeypot */}
                <input
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={state.website}
                  onChange={(e) => state.setWebsite(e.target.value)}
                  className="hidden"
                  aria-hidden="true"
                />
                {state.error && (
                  <p className="text-sm text-[#d9381e]">{state.error}</p>
                )}
                <button
                  type="submit"
                  data-testid="lead-magnet-a1-submit"
                  disabled={state.submitting}
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e2a5a] px-5 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e] disabled:opacity-60"
                >
                  {state.submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Download className="h-4 w-4" /> {t.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
